/* Shared HoK cart. Shopify owns payable lines; browser storage owns quote selections. */
(() => {
  'use strict';
  if (window.HOKCart) return;

  const STORAGE_KEY = 'hok-quote-cart-v1';
  const LEGACY_KEY = 'koval-industrial-equipment-cart-v1';
  const VESTIL_CHECKOUT_LIMIT = 250;
  const config = window.HOKCartConfig || {};
  const root = (config.root || '/').replace(/\/?$/, '/');
  const defaults = {
    loading: 'Loading your cart…', cart_error: 'Your cart could not be updated. Please try again.',
    quantity: 'Quantity', decrease_quantity: 'Decrease quantity', increase_quantity: 'Increase quantity',
    remove: 'Remove', unit_price: 'Unit price', line_total: 'Line total', model: 'Model',
    update_error: 'This change could not be saved. Please try again.', unavailable: 'This item is not available for online checkout.',
    no_items: 'Your cart is empty.', quote_empty: 'Select an item before requesting a quote.',
    quote_subtotal: 'Equipment subtotal', price_on_request: 'Price on request', added: 'Added to cart.',
    updated: 'Cart updated.', removed: 'Item removed.',
    resolution_error: 'We could not confirm this item. Refresh and try again.',
    route_error: 'This item needs a quote before payment. Remove it and add it again to request a quote.',
    storage_error: 'Your browser could not save these selections. Enable browser storage and try again.',
    cart_label: 'Cart, {{ count }} items', invalid_quantity: 'Enter a whole quantity of 1 or more.',
    quote_intro: 'Equipment selections for a delivered quote. Freight and tax will be confirmed before payment.',
    properties: 'Options', move_to_checkout: 'Move to checkout',
    checkout_quantity_limit: 'For more than 250 of this item, request a quote.',
    checkout_available: 'Online checkout is now available. You can keep this selection for a quote or move it to checkout.',
    saved_quote_title: 'Saved for a quote', saved_quote_intro: 'Request a quote for these selections, or move eligible items to checkout.',
    move_conflict: 'This selection could not be combined with your checkout items. Review their options and quantities before trying again.',
    equipment_subtotal: 'Equipment subtotal', commercial_freight: 'Estimated freight',
    known_industrial_freight: 'Estimated Industrial freight', before_tax_total: 'Estimated total before tax',
    checkout_tax_note: 'Includes estimated freight. Tax calculated at checkout.', mixed_shipping_note: 'Other shipping and tax calculated at checkout.',
    unknown_shipping_note: 'Freight and tax calculated at checkout.', delivery_details: 'Delivery details',
    commercial_delivery_terms: 'Lower 48 states + DC · Commercial truck access · Customer unloading',
    dispatch_estimate: 'Estimated dispatch', transit_estimate: 'Estimated transit: 5–15 business days after dispatch.',
    fence_dispatch_count: 'Fencing · {{ count }} complete kits',
    bulk_dispatch_note: 'Single-unit estimate; confirm timing for this quantity.',
    quote_selection_count: 'Your equipment ({{ count }})',
  };
  const label = (key, values = {}) => Object.entries(values).reduce(
    (value, [name, replacement]) => value.replaceAll(`{{ ${name} }}`, String(replacement)).replaceAll(`{{${name}}}`, String(replacement)),
    config.labels?.[key] || defaults[key] || key,
  );
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: state.cart.currency || config.currency || 'USD',
  }).format(cents / 100);
  let state = { ready: [], quote: [], cart: { items: [], item_count: 0, total_price: 0 }, hydrated: false, error: '' };
  let queue = Promise.resolve();
  let pending = 0;
  let lastOpener = null;
  const drafts = new Map();

  function report(error) {
    state.error = error instanceof Error ? error.message : label('cart_error');
    const target = document.querySelector('[data-hqc-error]');
    if (target) { target.textContent = state.error; target.hidden = false; }
    document.dispatchEvent(new CustomEvent('hok:cart-error', { detail: { message: state.error } }));
  }

  function enqueue(operation) {
    pending += 1;
    const result = queue.then(async () => {
      try { state.error = ''; return await operation(); }
      catch (error) { report(error); throw error; }
      finally { pending -= 1; if (!pending) render(); }
    });
    queue = result.catch(() => {});
    return result;
  }

  function quantity(value, allowZero = false) {
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < (allowZero ? 0 : 1)) throw new Error(label('invalid_quantity'));
    return number;
  }

  function modelMatch(input) {
    const models = Object.values(config.models || {});
    if (typeof input === 'string') return models.find(item => item.sku === input || String(item.variantId) === input);
    return models.find(item => (input.variantId && String(item.variantId) === String(input.variantId)) || (input.sku && item.sku === input.sku));
  }

  function identity(input) {
    const supplied = typeof input === 'string' ? { sku: input } : (input || {});
    const model = modelMatch(supplied) || {};
    const variantId = String(supplied.variantId || supplied.variant_id || model.variantId || '');
    let handle = String(supplied.handle || model.handle || '');
    if (!handle && supplied.url) {
      const match = safeUrl(supplied.url)?.pathname.match(/\/products\/([^/]+)/);
      if (match) handle = decodeURIComponent(match[1]);
    }
    return { variantId, handle, sku: String(supplied.sku || model.sku || ''), quantity: quantity(supplied.quantity ?? 1) };
  }

  function itemId(item) { return item.variantId ? `variant:${item.variantId}` : item.handle ? `product:${item.handle}:${item.sku}` : `sku:${item.sku}`; }

  function readStore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { version: 1, items: [], migratedLegacy: false };
      const data = JSON.parse(raw);
      if (!data || data.version !== 1 || !Array.isArray(data.items)) throw new Error('Invalid cart storage');
      return { version: 1, items: data.items.map(entry => ({ ...identity(entry), ...(entry.checkoutTransfer ? { checkoutTransfer: {
        quantity: quantity(entry.checkoutTransfer.quantity), targetQuantity: quantity(entry.checkoutTransfer.targetQuantity),
      } } : {}) })), migratedLegacy: Boolean(data.migratedLegacy) };
    } catch { throw new Error(label('storage_error')); }
  }

  function saveStore(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); }
    catch { throw new Error(label('storage_error')); }
  }

  async function mutateStore(change) {
    const mutate = () => { const data = readStore(); change(data); saveStore(data); return data; };
    return navigator.locks?.request ? navigator.locks.request(STORAGE_KEY, mutate) : mutate();
  }

  async function migrateLegacy() {
    let legacy;
    try { legacy = localStorage.getItem(LEGACY_KEY); }
    catch { throw new Error(label('storage_error')); }
    if (!legacy || readStore().migratedLegacy) return;
    let entries;
    try { entries = JSON.parse(legacy); if (!Array.isArray(entries)) throw new Error(); }
    catch { throw new Error(label('storage_error')); }
    await mutateStore(data => {
      if (data.migratedLegacy) return;
      for (const entry of entries) {
        if (!Array.isArray(entry) || entry.length < 2) throw new Error(label('storage_error'));
        const item = identity({ sku: String(entry[0]), quantity: quantity(Number(entry[1])) });
        const existing = data.items.find(saved => itemId(saved) === itemId(item));
        if (existing) existing.quantity = quantity(existing.quantity + item.quantity);
        else data.items.push(item);
      }
      data.migratedLegacy = true;
    });
    // The durable marker makes migration idempotent even if removing the old key fails.
    try { localStorage.removeItem(LEGACY_KEY); } catch { /* saved above */ }
  }

  function safeUrl(value, external = false) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, location.origin);
      if (!['http:', 'https:'].includes(url.protocol) || (!external && url.origin !== location.origin)) return null;
      return url;
    } catch { return null; }
  }

  async function jsonRequest(url, body, accept = 'application/json') {
    const response = await fetch(url, {
      credentials: 'same-origin', cache: 'no-store',
      headers: body ? { 'Content-Type': 'application/json', Accept: accept } : { Accept: accept },
      ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
    });
    let data;
    try { data = await response.json(); } catch { throw new Error(label('cart_error')); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : label('cart_error'));
    return data;
  }

  function resolver() {
    const products = new Map();
    return async input => {
      const item = identity(input);
      if (!item.handle || !/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(item.handle)) throw new Error(label('resolution_error'));
      // Shopify negotiates application/json into its native product API, bypassing this Liquid view.
      if (!products.has(item.handle)) products.set(item.handle, jsonRequest(`${root}products/${encodeURIComponent(item.handle)}?view=hok-purchase-route`, undefined, 'text/html'));
      const product = await products.get(item.handle);
      if (product.handle !== item.handle || !['quote_first', 'pay_online'].includes(product.route) || !Array.isArray(product.variants)) throw new Error(label('resolution_error'));
      const matches = product.variants.filter(variant => item.variantId ? String(variant.id) === item.variantId : item.sku ? variant.sku === item.sku : product.variants.length === 1);
      if (matches.length !== 1) throw new Error(label('resolution_error'));
      const variant = matches[0];
      if (!Number.isSafeInteger(Number(variant.price)) || Number(variant.price) < 0 || !variant.id) throw new Error(label('resolution_error'));
      const url = safeUrl(product.url || `${root}products/${item.handle}`);
      if (!url) throw new Error(label('resolution_error'));
      url.searchParams.set('variant', String(variant.id));
      return {
        ...item, variantId: String(variant.id), sku: variant.sku || item.sku, title: String(product.title || item.sku),
        variantTitle: variant.title && variant.title !== 'Default Title' ? String(variant.title) : '',
        route: product.route, vendor: String(product.vendor || ''), priceCents: Number(variant.price), available: Boolean(variant.available),
        image: safeUrl(variant.image, true)?.href || '', url: url.href,
      };
    };
  }

  function unresolved(item, source, key) {
    return { ...item, source, key, title: item.sku || item.handle || item.variantId, error: label('resolution_error'), priceCents: null };
  }

  async function refreshInternal() {
    await migrateLegacy();
    const resolve = resolver();
    const saved = readStore();
    const cart = await jsonRequest(`${root}cart.js`);
    if (!Array.isArray(cart.items)) throw new Error(label('cart_error'));
    const ready = [], quote = [];
    const localItems = await Promise.all(saved.items.map(async item => {
      const key = itemId(item);
      try { return { ...await resolve(item), source: 'local', key }; }
      catch { return unresolved(item, 'local', key); }
    }));
    quote.push(...localItems);
    await Promise.all(cart.items.map(async line => {
      const item = { variantId: String(line.variant_id || line.id || ''), handle: line.handle, sku: line.sku, quantity: quantity(line.quantity) };
      let resolved;
      try { resolved = await resolve(item); }
      catch { resolved = unresolved(item, 'native', line.key); }
      const native = {
        ...resolved, source: 'native', key: line.key, quantity: item.quantity,
        lineCents: Number(line.final_line_price), priceCents: Number(line.final_price),
        properties: line.properties || {}, discounts: line.line_level_discount_allocations || [],
      };
      if (native.route === 'quote_first') { native.warning = label('route_error'); quote.push(native); }
      else ready.push(native);
    }));
    // Keep Shopify's own line order rather than completion order of product requests.
    const order = new Map(cart.items.map((line, index) => [line.key, index]));
    ready.sort((a, b) => order.get(a.key) - order.get(b.key));
    for (const item of ready) {
      if (isVestil(item) && nativeQuantity(cart, item.variantId) > VESTIL_CHECKOUT_LIMIT) {
        item.checkoutLimitExceeded = true;
        item.warning = label('checkout_quantity_limit');
      }
    }
    state = { ...state, ready, quote, cart, hydrated: true };
    return state;
  }

  function canCheckout() {
    return state.hydrated && !state.error && state.ready.length > 0 && state.ready.every(item => !item.error && !item.checkoutLimitExceeded && item.route === 'pay_online') && !state.quote.some(item => item.source === 'native');
  }

  const isVestil = item => String(item.vendor || '').trim().toLowerCase() === 'vestil';
  const nativeQuantity = (cart, variantId) => cart.items.filter(line => String(line.variant_id || line.id) === String(variantId)).reduce((sum, line) => sum + Number(line.quantity), 0);
  function assertCheckoutQuantity(item, total) { if (isVestil(item) && total > VESTIL_CHECKOUT_LIMIT) throw new Error(label('checkout_quantity_limit')); }

  // The transition is limited to this supplier; existing saved inquiries remain intentional.
  function movable(item) { return !item.error && item.source === 'local' && item.route === 'pay_online' && item.available && isVestil(item); }

  async function moveSaved(key, extraQuantity = 0) {
    const move = async () => {
      const data = readStore(), saved = data.items.find(item => itemId(item) === key);
      if (!saved) return false;
      const item = { ...await resolver()(saved), source: 'local' };
      if (!movable(item)) throw new Error(label('unavailable'));
      const cart = await jsonRequest(`${root}cart.js`);
      const matches = cart.items.filter(line => String(line.variant_id || line.id) === item.variantId);
      // Never merge into a customized or subscription line.
      const plain = matches.filter(line => !Object.values(line.properties || {}).some(value => value !== null && value !== '') && !line.selling_plan_allocation);
      if (plain.length > 1 || (matches.length && !plain.length)) throw new Error(label('move_conflict'));
      const current = plain[0], currentQuantity = current?.quantity || 0;
      const targetQuantity = saved.checkoutTransfer?.targetQuantity ?? quantity(currentQuantity + saved.quantity + extraQuantity);
      assertCheckoutQuantity(item, nativeQuantity(cart, item.variantId) - currentQuantity + targetQuantity);
      if (!saved.checkoutTransfer) {
        saved.checkoutTransfer = { quantity: saved.quantity, targetQuantity };
        // Persist the absolute target before the request, so an interrupted response cannot double-add on retry.
        saveStore(data);
      }
      const transfer = saved.checkoutTransfer;
      if (currentQuantity > transfer.targetQuantity) throw new Error(label('move_conflict'));
      if (currentQuantity !== transfer.targetQuantity) {
        const updated = await jsonRequest(`${root}cart/update.js`, { updates: { [current?.key || item.variantId]: transfer.targetQuantity } });
        const confirmedQuantity = (updated.items || []).filter(line => String(line.variant_id || line.id) === item.variantId && !Object.values(line.properties || {}).some(value => value !== null && value !== '') && !line.selling_plan_allocation).reduce((sum, line) => sum + line.quantity, 0);
        if (confirmedQuantity !== transfer.targetQuantity) throw new Error(label('update_error'));
      }
      const latest = readStore(), remaining = latest.items.find(entry => itemId(entry) === key);
      if (remaining) {
        remaining.quantity -= transfer.quantity;
        delete remaining.checkoutTransfer;
        latest.items = latest.items.filter(entry => entry.quantity > 0);
        saveStore(latest);
      }
      return true;
    };
    return navigator.locks?.request ? navigator.locks.request(STORAGE_KEY, move) : move();
  }

  const lineTotal = item => item.source === 'native' && Number.isFinite(item.lineCents) ? item.lineCents : item.priceCents === null ? null : item.priceCents * item.quantity;
  function subtotal(items) { return items.some(item => item.error || lineTotal(item) === null) ? null : items.reduce((sum, item) => sum + lineTotal(item), 0); }
  function quoteSubtotal(items) {
    const native = items.filter(item => item.source === 'native');
    if (native.length && native.length === state.cart.items.length && state.cart.items.every(line => native.some(item => item.key === line.key))) {
      return Number(state.cart.total_price) + subtotal(items.filter(item => item.source !== 'native'));
    }
    return subtotal(items);
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  // Only join a native line to a freight charge when both catalog and live Liquid
  // identity agree. A matching SKU alone is not enough to price an unknown variant.
  function freightModel(item) {
    if (item.error || item.source !== 'native' || item.route !== 'pay_online' || !isVestil(item)) return null;
    const identities = Object.values(config.models || {}).filter(model => model.sku === item.sku && String(model.variantId) === item.variantId && model.handle === item.handle);
    const models = (Array.isArray(window.KOVAL_CATALOG?.models) ? window.KOVAL_CATALOG.models : []).filter(model => model.model === item.sku);
    if (identities.length !== 1 || models.length !== 1) return null;
    const model = models[0];
    if (model.shopify?.variantId && String(model.shopify.variantId) !== item.variantId) return null;
    return model;
  }

  function freightSummary() {
    const known = [];
    if (state.cart.currency === 'USD') for (const item of state.ready) {
      const model = freightModel(item), charge = model?.shipping?.retail_charge_usd;
      if (!model || model.currency !== 'USD' || typeof charge !== 'number' || !Number.isFinite(charge) || charge < 0 || item.checkoutLimitExceeded) continue;
      const cents = Math.round(charge * 100);
      if (!Number.isSafeInteger(cents) || Math.abs(cents - charge * 100) > .000001 || !Number.isSafeInteger(cents * item.quantity)) continue;
      known.push({ item, model, cents: cents * item.quantity });
    }
    const cents = known.reduce((sum, entry) => sum + entry.cents, 0);
    const total = Number(state.cart.total_price);
    const complete = canCheckout() && known.length === state.ready.length && state.ready.length === state.cart.items.length && Number.isSafeInteger(total) && total >= 0 && Number.isSafeInteger(total + cents);
    return { known, cents, complete, total: complete ? total + cents : null };
  }

  function renderFreight(container) {
    const panel = container.querySelector('[data-hqc-group="ready"]');
    const summary = panel?.querySelector('.hqc-summary');
    if (!summary) return;
    const freight = freightSummary(), hasIndustrial = state.ready.some(isVestil);
    const row = summary.querySelector('[data-hqc-freight-row]');
    const grand = summary.querySelector('[data-hqc-grand-total]');
    const note = summary.querySelector('[data-hqc-checkout-note]');
    const subtotalLabel = summary.querySelector('[data-hqc-subtotal-label]');
    const details = summary.querySelector('[data-hqc-delivery-details]');
    summary.classList.toggle('hqc-summary--freight', freight.known.length > 0);
    if (row) {
      row.hidden = !freight.known.length;
      row.querySelector('[data-hqc-freight-label]').textContent = label(freight.complete ? 'commercial_freight' : 'known_industrial_freight');
      row.querySelector('[data-hqc-freight-amount]').textContent = money(freight.cents);
    }
    if (grand) {
      grand.hidden = !freight.complete;
      grand.querySelector('[data-hqc-total-amount]').textContent = freight.complete ? money(freight.total) : '';
    }
    if (note) {
      note.dataset.originalText ??= note.textContent;
      note.textContent = !hasIndustrial ? note.dataset.originalText : label(freight.complete ? 'checkout_tax_note' : freight.known.length ? 'mixed_shipping_note' : 'unknown_shipping_note');
    }
    if (subtotalLabel) {
      subtotalLabel.dataset.originalText ??= subtotalLabel.textContent;
      subtotalLabel.textContent = freight.complete ? label('equipment_subtotal') : subtotalLabel.dataset.originalText;
    }
    if (details) {
      details.hidden = !freight.known.length;
      const dispatch = details.querySelector('[data-hqc-dispatch]');
      if (dispatch) {
        const lines = [], fences = freight.known.filter(({ model }) => model.family_id === 'temporary-fencing');
        for (const { item, model } of freight.known.filter(({ model }) => model.family_id !== 'temporary-fencing')) {
          let timing = model.lead_label || model.lead_text;
          if (timing && nativeQuantity(state.cart, item.variantId) > 1) timing += `. ${label('bulk_dispatch_note')}`;
          if (timing && !lines.some(line => line.sku === item.sku)) lines.push({ sku: item.sku, title: item.sku, timing });
        }
        if (fences.length) {
          const kits = fences.reduce((sum, { item, model }) => sum + item.quantity * Number(model.kit_quantity), 0);
          // Select a quantity clause from catalog text, across all kit packages in
          // this cart. If the source format changes, retain its full conditions.
          const fenceModels = window.KOVAL_CATALOG.models.filter(model => model.family_id === 'temporary-fencing');
          const clauses = fenceModels.flatMap(model => (model.lead_text || '').split(';').map(text => text.trim()));
          const tier = clauses.find(text => {
            const range = text.match(/for\s+(\d+)\s*[–-]\s*(\d+)(?:\s+kits)?/i);
            return range && kits >= Number(range[1]) && kits <= Number(range[2]);
          });
          const largeOrder = fenceModels.find(model => model.lead_days === null)?.lead_text;
          const timing = tier || largeOrder || [...new Set(fences.map(({ model }) => model.lead_text || model.lead_label).filter(Boolean))].join(' ');
          if (timing) lines.push({ title: label('fence_dispatch_count', { count: kits }), timing });
        }
        dispatch.replaceChildren(...lines.map(line => {
          const row = element('li');
          row.append(element('strong', '', line.title), element('span', '', line.timing));
          return row;
        }));
      }
    }
  }

  function renderItem(item) {
    const row = element('article', 'hqc-item');
    row.dataset.hqcKey = item.key;
    row.dataset.hqcSource = item.source;
    const picture = element(item.url ? 'a' : 'div', 'hqc-item__image');
    if (item.url) picture.href = item.url;
    if (item.image) {
      const image = element('img'); image.src = item.image; image.alt = item.title; image.width = 144; image.height = 120; image.loading = 'lazy'; picture.append(image);
    }
    row.append(picture);
    const details = element('div', 'hqc-item__details');
    const title = element(item.url ? 'a' : 'span', 'hqc-item__title', item.title);
    if (item.url) title.href = item.url;
    details.append(title);
    if (item.sku) details.append(element('p', 'hqc-item__meta', `${label('model')}: ${item.sku}`));
    if (item.variantTitle) details.append(element('p', 'hqc-item__meta', item.variantTitle));
    for (const [key, value] of Object.entries(item.properties || {})) {
      if (!key.startsWith('_') && value !== null && value !== '') details.append(element('p', 'hqc-item__meta', `${key}: ${value}`));
    }
    for (const allocation of item.discounts || []) {
      const discount = allocation.discount_application?.title;
      if (discount) details.append(element('p', 'hqc-item__meta', discount));
    }
    details.append(element('p', 'hqc-item__meta', `${label('unit_price')}: ${item.error || item.priceCents === null ? label('price_on_request') : money(item.priceCents)}`));
    if (item.error || item.warning) details.append(element('p', 'hqc-item__error', item.error || item.warning));
    if (movable(item)) details.append(element('p', 'hqc-item__meta', label('checkout_available')));
    row.append(details);
    const controls = element('div', 'hqc-item__controls');
    const qty = element('div', 'hqc-item__quantity');
    const input = element('input');
    input.type = 'number'; input.min = '1'; input.step = '1'; input.inputMode = 'numeric'; input.value = String(drafts.get(draftKey(item.source, item.key))?.value ?? item.quantity);
    if (item.source === 'native' && isVestil(item)) input.max = String(VESTIL_CHECKOUT_LIMIT);
    input.dataset.hqcQuantity = item.key; input.dataset.hqcSource = item.source;
    input.setAttribute('aria-label', `${label('quantity')}: ${item.sku || item.title}`);
    for (const [step, text, key] of [[-1, '−', 'decrease_quantity'], [1, '+', 'increase_quantity']]) {
      const button = element('button', '', text); button.type = 'button'; button.dataset.hqcStep = String(step); button.setAttribute('aria-label', `${label(key)}: ${item.sku || item.title}`);
      if (step === -1) { button.disabled = item.quantity <= 1; qty.append(button, input); }
      else { button.disabled = item.source === 'native' && isVestil(item) && nativeQuantity(state.cart, item.variantId) >= VESTIL_CHECKOUT_LIMIT; qty.append(button); }
    }
    const remove = element('button', 'hqc-item__remove', label('remove')); remove.type = 'button'; remove.dataset.hqcRemove = item.key; remove.dataset.hqcSource = item.source;
    remove.setAttribute('aria-label', `${label('remove')}: ${item.sku || item.title}`);
    controls.append(qty, remove);
    if (movable(item)) {
      const move = element('button', 'hqc-button hqc-button--secondary', label('move_to_checkout'));
      move.type = 'button'; move.dataset.hqcMove = item.key;
      move.setAttribute('aria-label', `${label('move_to_checkout')}: ${item.sku || item.title}`);
      controls.append(move);
    }
    details.append(controls);
    row.append(element('strong', 'hqc-item__price', item.error || lineTotal(item) === null ? label('price_on_request') : money(lineTotal(item))));
    return row;
  }

  function render() {
    const count = [...state.ready, ...state.quote].reduce((sum, item) => sum + item.quantity, 0);
    if (state.hydrated) {
      for (const node of document.querySelectorAll('[data-hqc-count], #cart-count')) node.textContent = String(count);
      for (const node of document.querySelectorAll('[data-hqc-cart-link], [data-hqc-count]')) {
        const link = node.matches('[data-hqc-cart-link]') ? node : node.closest('a, button');
        if (link) link.setAttribute('aria-label', label('cart_label', { count }));
      }
      document.dispatchEvent(new CustomEvent('hok:cart-updated', { detail: { count, readyCount: state.ready.reduce((sum, item) => sum + item.quantity, 0), quoteCount: state.quote.reduce((sum, item) => sum + item.quantity, 0) } }));
    }
    const container = document.getElementById('hok-shared-cart');
    if (!container) return;
    const focused = document.activeElement;
    const focusKey = focused?.dataset?.hqcQuantity;
    const quoteTitle = container.querySelector('#hqc-freight-title');
    if (quoteTitle) {
      const intro = quoteTitle.nextElementSibling;
      quoteTitle.dataset.originalText ??= quoteTitle.textContent;
      if (intro) intro.dataset.originalText ??= intro.textContent;
      const hasPayableSelection = state.quote.some(movable);
      quoteTitle.textContent = hasPayableSelection ? label('saved_quote_title') : quoteTitle.dataset.originalText;
      if (intro) intro.textContent = hasPayableSelection ? label('saved_quote_intro') : intro.dataset.originalText;
    }
    for (const group of ['ready', 'quote']) {
      const items = state[group];
      const list = container.querySelector(`[data-hqc-items="${group}"]`);
      if (list) list.replaceChildren(...items.map(renderItem));
      const panel = container.querySelector(`[data-hqc-group="${group}"]`);
      if (panel) panel.hidden = !items.length;
      const amount = container.querySelector(`[data-hqc-subtotal="${group}"]`);
      const total = group === 'ready' && !state.quote.some(item => item.source === 'native') ? Number(state.cart.total_price) : subtotal(items);
      if (amount) amount.textContent = total === null || !Number.isFinite(total) ? label('price_on_request') : money(total);
    }
    renderFreight(container);
    if (focusKey) for (const input of container.querySelectorAll('[data-hqc-quantity]')) if (input.dataset.hqcQuantity === focusKey) input.focus({ preventScroll: true });
    const loading = container.querySelector('[data-hqc-loading]'); if (loading) loading.hidden = true;
    const fallback = container.querySelector('[data-hqc-fallback]'); if (fallback) fallback.hidden = state.hydrated;
    const empty = container.querySelector('[data-hqc-empty]'); if (empty) empty.hidden = !state.hydrated || count > 0;
    const all = container.querySelector('[data-hqc-all]'); if (all) all.hidden = !state.ready.length || !state.quote.length;
    const error = container.querySelector('[data-hqc-error]'); if (error) { error.textContent = state.error; error.hidden = !state.error; }
    for (const button of container.querySelectorAll('[data-hqc-checkout]')) button.disabled = !canCheckout();
    for (const button of container.querySelectorAll('[data-hqc-request]')) {
      const items = selection(button.dataset.hqcRequest);
      button.disabled = !state.hydrated || !items.length || items.some(item => item.error);
    }
  }

  function selection(group) { return group === 'all' ? [...state.ready, ...state.quote] : state[group] || []; }

  async function changeInternal(source, key, value) {
    const next = quantity(value, true);
    if (source === 'native') {
      if (next) {
        const cart = await jsonRequest(`${root}cart.js`), line = cart.items.find(item => item.key === key);
        if (!line) throw new Error(label('update_error'));
        const item = await resolver()({ variantId: String(line.variant_id || line.id), handle: line.handle, sku: line.sku });
        assertCheckoutQuantity(item, nativeQuantity(cart, item.variantId) - line.quantity + next);
      }
      await jsonRequest(`${root}cart/change.js`, { id: key, quantity: next });
    }
    else await mutateStore(data => {
      const item = data.items.find(entry => itemId(entry) === key);
      if (!item) throw new Error(label('update_error'));
      if (!next) data.items = data.items.filter(entry => itemId(entry) !== key);
      else { if (item.checkoutTransfer) throw new Error(label('move_conflict')); item.quantity = next; }
    });
  }

  function draftKey(source, key) { return `${source}|${key}`; }
  function collectDrafts() {
    for (const input of document.querySelectorAll('#hok-shared-cart [data-hqc-quantity]')) {
      const source = input.dataset.hqcSource, key = input.dataset.hqcQuantity;
      const current = [...state.ready, ...state.quote].find(item => item.source === source && item.key === key);
      if (current && Number(input.value) !== current.quantity) drafts.set(draftKey(source, key), { source, key, value: input.value });
    }
  }
  async function flushDrafts() {
    const changes = [...drafts.entries()];
    for (const [key, draft] of changes) {
      await changeInternal(draft.source, draft.key, draft.value);
      if (drafts.get(key) === draft) drafts.delete(key);
    }
  }

  async function add(input, qty = 1) {
    return enqueue(async () => {
      const item = await resolver()({ ...identity(input), quantity: quantity(qty) });
      if (item.route === 'quote_first') {
        await migrateLegacy();
        await mutateStore(data => {
          const existing = data.items.find(entry => itemId(entry) === itemId(item));
          if (existing) existing.quantity = quantity(existing.quantity + item.quantity);
          else data.items.push(identity(item));
        });
      } else {
        if (!item.available) throw new Error(label('unavailable'));
        await migrateLegacy();
        const moved = movable({ ...item, source: 'local' }) && await moveSaved(itemId(item), item.quantity);
        if (!moved) {
          if (isVestil(item)) assertCheckoutQuantity(item, nativeQuantity(await jsonRequest(`${root}cart.js`), item.variantId) + item.quantity);
          await jsonRequest(`${root}cart/add.js`, { items: [{ id: item.variantId, quantity: item.quantity }] });
        }
      }
      await refreshInternal();
      return item;
    });
  }

  function fillQuote(items) {
    if (!items.length) throw new Error(label('quote_empty'));
    if (items.some(item => item.error || !item.variantId || !item.url)) throw new Error(label('resolution_error'));
    const dialog = document.getElementById('hok-quote-dialog');
    if (!dialog) throw new Error(label('cart_error'));
    const lines = items.map(item => [
      `${item.quantity} × ${item.title}${item.variantTitle ? ` — ${item.variantTitle}` : ''}`,
      `${label('model')}: ${item.sku || item.variantId}`,
      `${label('unit_price')}: ${money(item.priceCents)}; ${label('line_total')}: ${money(lineTotal(item))}`,
      ...Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value !== null && value !== '').map(([key, value]) => `${key}: ${value}`),
      item.url,
    ].join('\n'));
    const summary = `${label('quote_intro')}\n\n${lines.join('\n\n')}\n\n${label('quote_subtotal')}: ${money(quoteSubtotal(items))}`;
    const values = { items: summary, model: items.map(item => item.sku || item.variantId).join(', '), quantity: String(items.reduce((sum, item) => sum + item.quantity, 0)), url: items.map(item => item.url).join('\n') };
    for (const node of dialog.querySelectorAll('[data-hqc-field]')) if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) node.value = values[node.dataset.hqcField] || '';
    for (const node of dialog.querySelectorAll('[data-hqc-summary]')) node.textContent = summary;
    for (const node of dialog.querySelectorAll('[data-hqc-selection-label]')) node.textContent = label('quote_selection_count', { count: items.reduce((sum, item) => sum + item.quantity, 0) });
    for (const node of dialog.querySelectorAll('[data-hqc-success], [data-hqc-errors]')) node.hidden = true;
    for (const node of dialog.querySelectorAll('[data-hqc-fields]')) node.hidden = false;
    for (const node of dialog.querySelectorAll('[data-hqc-server-status]')) node.dataset.hqcServerStatus = 'ready';
    try { sessionStorage.setItem('hok-quote-context-v1', JSON.stringify(values)); } catch { /* The form still contains its current equipment fields. */ }
    dialog.querySelector('form')?.dispatchEvent(new CustomEvent('hok:quote-prepared', { bubbles: true }));
    lastOpener = document.activeElement;
    if (!dialog.open) dialog.showModal();
    return items;
  }

  function requestQuote(items) {
    collectDrafts();
    return enqueue(async () => {
      await flushDrafts();
      if (!Array.isArray(items)) {
        await refreshInternal();
        return fillQuote(selection(typeof items === 'string' ? items : 'all'));
      }
      const resolve = resolver();
      const resolved = await Promise.all(items.map(async item => ({ ...await resolve(item), source: 'local' })));
      return fillQuote(resolved);
    });
  }

  function refresh() { return enqueue(refreshInternal); }
  function open() { location.assign(config.cartUrl || `${root}cart`); }
  window.HOKCart = { add, requestQuote, refresh, open };

  function restoreQuoteReturn() {
    const query = new URLSearchParams(location.search);
    if (query.get('hok_quote') !== '1' && location.hash !== '#HoKSharedQuoteForm') return;
    const dialog = document.getElementById('hok-quote-dialog');
    const status = dialog?.querySelector('[data-hqc-server-status]')?.dataset.hqcServerStatus;
    if (!dialog || !['success', 'error'].includes(status)) return;
    if (status === 'error') {
      let saved = {};
      try { saved = JSON.parse(sessionStorage.getItem('hok-quote-context-v1') || '{}'); } catch { /* Use server-rendered fields when available. */ }
      for (const node of dialog.querySelectorAll('[data-hqc-field]')) {
        if ((node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) && !node.value && typeof saved?.[node.dataset.hqcField] === 'string') node.value = saved[node.dataset.hqcField];
      }
      const summary = dialog.querySelector('[data-hqc-summary]');
      if (summary) summary.textContent = dialog.querySelector('[data-hqc-field="items"]')?.value || '';
    }
    if (!dialog.open) dialog.showModal();
    dialog.querySelector(status === 'success' ? '[data-hqc-success]' : '[data-hqc-errors]')?.focus();
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'HoKSharedQuoteForm' || event.defaultPrevented || form.hasAttribute('data-hok-inquiry')) return;
    if (!form.querySelector('[data-hqc-field="items"]')?.value?.trim() || form.dataset.hqcSubmitting === 'true') {
      event.preventDefault();
      if (form.dataset.hqcSubmitting !== 'true') report(new Error(label('quote_empty')));
      return;
    }
    form.dataset.hqcSubmitting = 'true';
    const submit = form.querySelector('[data-hqc-submit]');
    if (submit) { submit.disabled = true; submit.textContent = label('submitting'); }
  });

  document.addEventListener('input', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('#hok-shared-cart [data-hqc-quantity]')) return;
    drafts.set(draftKey(input.dataset.hqcSource, input.dataset.hqcQuantity), { source: input.dataset.hqcSource, key: input.dataset.hqcQuantity, value: input.value });
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('#hok-shared-cart [data-hqc-quantity]')) return;
    drafts.set(draftKey(input.dataset.hqcSource, input.dataset.hqcQuantity), { source: input.dataset.hqcSource, key: input.dataset.hqcQuantity, value: input.value });
    enqueue(async () => { await flushDrafts(); await refreshInternal(); }).catch(() => {});
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-hqc-step], [data-hqc-remove], [data-hqc-checkout], [data-hqc-request], [data-hqc-close], [data-hqc-move]');
    if (!button) return;
    event.preventDefault();
    if (button.hasAttribute('data-hqc-close')) { document.getElementById('hok-quote-dialog')?.close(); return; }
    if (button.hasAttribute('data-hqc-request')) { requestQuote(button.dataset.hqcRequest).catch(() => {}); return; }
    if (button.hasAttribute('data-hqc-move')) {
      if (button.disabled) return;
      button.disabled = true;
      collectDrafts();
      enqueue(async () => { await flushDrafts(); await moveSaved(button.dataset.hqcMove); await refreshInternal(); }).catch(() => {});
      return;
    }
    if (button.hasAttribute('data-hqc-checkout')) {
      collectDrafts();
      enqueue(async () => { await flushDrafts(); await refreshInternal(); if (state.ready.some(item => item.checkoutLimitExceeded)) throw new Error(label('checkout_quantity_limit')); if (!canCheckout()) throw new Error(label('resolution_error')); location.assign(config.checkoutUrl || `${root}checkout`); }).catch(() => {});
      return;
    }
    if (button.hasAttribute('data-hqc-step')) {
      const input = button.closest('.hqc-item__quantity')?.querySelector('input');
      if (!input) return;
      input.value = String(Math.max(1, Number(input.value || 1) + Number(button.dataset.hqcStep)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    drafts.delete(draftKey(button.dataset.hqcSource, button.dataset.hqcRemove));
    enqueue(async () => { await changeInternal(button.dataset.hqcSource, button.dataset.hqcRemove, 0); await refreshInternal(); }).catch(() => {});
  });

  document.addEventListener('close', event => {
    if (event.target?.id !== 'hok-quote-dialog') return;
    const replacement = lastOpener?.dataset?.hqcRequest && [...document.querySelectorAll('[data-hqc-request]')].find(button => button.dataset.hqcRequest === lastOpener.dataset.hqcRequest);
    const opener = lastOpener?.isConnected ? lastOpener : replacement;
    opener?.focus({ preventScroll: true });
  }, true);
  for (const name of ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'cart:updated', 'cart:refresh']) document.addEventListener(name, async event => {
    try { if (event.promise) await event.promise; await refresh(); } catch (error) { report(error); }
  });
  window.addEventListener('storage', event => { if ([STORAGE_KEY, LEGACY_KEY, null].includes(event.key)) refresh().catch(() => {}); });
  window.addEventListener('pageshow', () => {
    const form = document.getElementById('HoKSharedQuoteForm');
    if (form) { delete form.dataset.hqcSubmitting; const submit = form.querySelector('[data-hqc-submit]'); if (submit) { submit.disabled = false; submit.textContent = label('submit_request'); } }
    refresh().catch(() => {});
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refresh().catch(() => {}); });
  document.addEventListener('shopify:section:load', () => refresh().catch(() => {}));
  refresh().catch(() => {});
  restoreQuoteReturn();
})();
